import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import axios from "axios";
import { ArrowLeft, Phone, User, LogOut } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [currentUser, setCurrentUser] = useState(location.state?.user || null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      fetchContacts();
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        if (!response.data.is_admin) {
          alert("Šim Google kontam nav admin tiesību.");
          navigate("/");
          return;
        }
        setCurrentUser(response.data);
        setIsAuthenticated(true);
        fetchContacts();
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        withCredentials: true,
      });
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("lv-LV", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isAuthenticated === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#F6F8F3" }}
      >
        <p style={{ color: "#475545" }}>Ielādē...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F6F8F3" }}>
      <div className="max-w-6xl mx-auto px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-4xl font-black tracking-tight"
              style={{ color: "#1A2118" }}
              data-testid="admin-title"
            >
              Admin panelis
            </h1>
            <p style={{ color: "#475545" }} className="mt-2">
              Klientu pieteikumi
              {currentUser && (
                <span className="ml-2 text-sm">({currentUser.email})</span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/")}
              data-testid="back-to-home-button"
              className="rounded-full"
              style={{ backgroundColor: "#27A64C", color: "white" }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Atpakaļ
            </Button>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="outline"
              className="rounded-full"
              style={{ borderColor: "#27A64C", color: "#1A2118" }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Iziet
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12" style={{ color: "#475545" }}>
            Ielādē...
          </div>
        ) : contacts.length === 0 ? (
          <Card
            className="p-12 text-center"
            style={{ backgroundColor: "#FFFFFF", borderRadius: "24px" }}
            data-testid="no-contacts-message"
          >
            <p style={{ color: "#475545" }}>Nav neviena pieteikuma</p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="contacts-list">
            {contacts.map((contact, index) => (
              <Card
                key={index}
                data-testid={`contact-card-${index}`}
                className="p-6 shadow-md hover:shadow-lg transition-shadow duration-200"
                style={{ backgroundColor: "#FFFFFF", borderRadius: "24px" }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5" style={{ color: "#27A64C" }} />
                      <span className="font-bold" style={{ color: "#1A2118" }}>
                        {contact.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-5 w-5" style={{ color: "#27A64C" }} />
                      <a
                        href={`tel:${contact.phone}`}
                        className="hover:underline"
                        style={{ color: "#475545" }}
                      >
                        {contact.phone}
                      </a>
                    </div>
                  </div>
                  <div className="text-sm" style={{ color: "#475545" }}>
                    {formatDate(contact.timestamp)}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
